/**
 * Sleepio Activation API (vercel-auth-functions-lower)
 *
 * Centralizes all VAF calls for the Sleepio activation flow.
 * Product is fixed to 'sleepio'; lob is passed per call.
 *
 * Reference pattern: src/utils/sparkActivationApi.ts
 */
import { logError } from './logError';
import { activateEmail as activateEmailGeneric, buildVafUrl, requestAuthCode } from './vafAuthApi';

import type { LineOfBusiness } from './constants';

const PRODUCT = 'sleepio';

interface ActivatePhoneParams {
  phone: string;
  organizationSlug: string;
  lob?: LineOfBusiness;
  phDistinctId?: string;
}

interface ActivatePhoneResponse {
  success: boolean;
  message: string;
  limbix_user_id: string;
}

interface ActivateConfirmParams {
  accessToken: string;
  lob?: LineOfBusiness;
}

interface ActivateEmailParams {
  email: string;
  organizationSlug: string;
  lob?: LineOfBusiness;
  redirectTo?: string;
  phDistinctId?: string;
}

interface ActivateEmailResponse {
  success: boolean;
  limbix_user_id: string;
}

/**
 * Calls POST /api/sleepio/{lob}/activate/phone to register the user server-side.
 * vercel-auth-functions sends the OTP itself as part of this call (PAT-1125) —
 * callers should navigate straight to OTP verification on success, not call
 * supabase.auth.signInWithOtp() again for the same activation.
 * Sleepio uses the direct access path (organization_slug), not access codes.
 */
export const activateSleepioPhone = async (
  params: ActivatePhoneParams,
): Promise<ActivatePhoneResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = buildVafUrl(`/api/${PRODUCT}/${lob}/activate/phone`);

  const body: Record<string, string> = {
    phone: params.phone,
    organization_slug: params.organizationSlug,
  };

  if (params.phDistinctId) {
    body.ph_distinct_id = params.phDistinctId;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    logError(
      '[activateSleepioPhone]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Sleepio phone activation failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Calls POST /api/sleepio/{lob}/activate/email to trigger a magic link email.
 * Sleepio uses the direct access path (organization_slug), not access codes.
 */
export const activateSleepioEmail = (
  params: ActivateEmailParams,
): Promise<ActivateEmailResponse> => activateEmailGeneric({
  email: params.email,
  organizationSlug: params.organizationSlug,
  lob: params.lob,
  redirectTo: params.redirectTo,
  phDistinctId: params.phDistinctId,
  product: PRODUCT,
});

/**
 * Calls POST /api/sleepio/{lob}/activate/confirm to flip user_app_entitlements.status → active.
 * Must be called AFTER supabase.auth.verifyOtp() or the email callback establishes a session.
 */
export const activateSleepioConfirm = async (
  params: ActivateConfirmParams,
): Promise<void> => {
  const lob = params.lob ?? 'wellness';
  const url = buildVafUrl(`/api/${PRODUCT}/${lob}/activate/confirm`);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken}`,
    },
  });

  if (!response.ok) {
    logError(
      '[activateSleepioConfirm]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Sleepio confirm failed: ${response.status}`);
  }
};

/**
 * Obtains a short-lived exchange token for session handoff to Canopy.
 * Implemented in DAYL-1134. Delegates to the shared requestAuthCode utility.
 */
export const activateSleepioAuthorize = requestAuthCode;
